import { useScienceTutorSection, tutorSelection } from './scienceTutorContext';
import { Factory } from 'lucide-react';
import { Fragment, useState } from 'react';

import { prettyChemistry } from './fuelsSustainability';
import {
  MAX_COEFFICIENT,
  MIN_COEFFICIENT,
  PRODUCTION_CASES,
  PRODUCTION_ROUTE_LABELS,
  atomTally,
  checkProductionBalance,
  productionSpecies,
  skeletonCoefficients,
  type BalanceCheck,
  type ProductionCase,
  type ProductionSpecies,
} from './fuelsSustainabilityProcesses';
import { CaseList, FeedbackPanel } from './FuelsSustainabilityShared';

const STATUS_TITLES: Record<BalanceCheck['status'], string> = {
  balanced: 'Balanced in the smallest whole-number ratio.',
  'not-simplest': 'Balanced, but not in the simplest ratio.',
  unbalanced: 'Not balanced yet.',
  invalid: 'Check the coefficients.',
};

function speciesLabel(item: ProductionSpecies): string {
  return `${prettyChemistry(item.formula)}${item.state ? `(${item.state})` : ''}`;
}

function CoefficientInput({
  item,
  value,
  onChange,
}: {
  item: ProductionSpecies;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <label className="inline-flex min-w-0 items-center gap-1.5 rounded-2xl border border-hairline bg-surface px-2 py-1.5">
      <input
        type="number"
        inputMode="numeric"
        min={MIN_COEFFICIENT}
        max={MAX_COEFFICIENT}
        step={1}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        aria-label={`Coefficient for ${item.formula} (${item.name})`}
        className="w-12 rounded-xl border border-hairline bg-canvas-pure px-1.5 py-1 text-center text-sm font-extrabold text-ink"
      />
      <span className="flex min-w-0 flex-col">
        <span className="break-all font-mono text-sm font-bold text-ink">{speciesLabel(item)}</span>
        <span className="text-[11px] font-bold leading-4 text-slate2">{item.name}</span>
      </span>
    </label>
  );
}

export function FuelsSustainabilityProduction() {
  const [selected, setSelected] = useState<ProductionCase>(PRODUCTION_CASES[0]);
  const [values, setValues] = useState<string[]>(() =>
    skeletonCoefficients(PRODUCTION_CASES[0]).map(String),
  );
  useScienceTutorSection('FuelsSustainabilityProduction', { selected: tutorSelection(selected), values });
  const [check, setCheck] = useState<BalanceCheck | null>(null);
  const coefficients = values.map((value) => (value.trim() === '' ? Number.NaN : Number(value)));
  const tally = atomTally(selected, coefficients);
  const items = productionSpecies(selected);

  const choose = (id: string) => {
    const next = PRODUCTION_CASES.find((entry) => entry.id === id);
    if (!next) return;
    setSelected(next);
    setValues(skeletonCoefficients(next).map(String));
    setCheck(null);
  };

  const update = (index: number, value: string) => {
    setValues((current) => current.map((item, position) => (position === index ? value : item)));
    setCheck(null);
  };

  return (
    <div className="grid gap-6 xl:grid-cols-[320px_minmax(0,1fr)]">
      <CaseList
        heading="Choose a process"
        label="Fuel production processes"
        items={PRODUCTION_CASES.map((entry) => ({
          id: entry.id,
          label: entry.name,
          detail: PRODUCTION_ROUTE_LABELS[entry.route],
        }))}
        selectedId={selected.id}
        onSelect={choose}
      />

      <section className="min-w-0 rounded-[1.75rem] border border-hairline bg-canvas-pure p-5 shadow-card-soft sm:p-6">
        <p className="flex items-center gap-2 text-xs font-extrabold uppercase tracking-[0.14em] text-brand-coral">
          <Factory className="h-4 w-4" aria-hidden="true" /> {PRODUCTION_ROUTE_LABELS[selected.route]}
        </p>
        <h3 className="mt-1 text-xl font-extrabold text-ink">{selected.name}</h3>
        <p className="mt-1 text-sm font-medium leading-6 text-slate2">
          Balance the equation by changing coefficients only. Formulae stay fixed.
        </p>

        <div className="mt-4 flex flex-wrap items-center gap-2" aria-label="Equation to balance">
          {items.map((item, index) => (
            <Fragment key={`${item.formula}-${index}`}>
              {index > 0 ? (
                <span className="text-lg font-extrabold text-ink" aria-hidden="true">
                  {index === selected.reactants.length ? '→' : '+'}
                </span>
              ) : null}
              <CoefficientInput
                item={item}
                value={values[index] ?? ''}
                onChange={(value) => update(index, value)}
              />
            </Fragment>
          ))}
        </div>

        <div className="mt-5 overflow-x-auto">
          <table className="w-full min-w-[260px] text-left text-sm" aria-label="Atom count">
            <thead>
              <tr className="text-xs font-extrabold uppercase tracking-[0.1em] text-slate2">
                <th className="py-1 pr-3">Element</th>
                <th className="py-1 pr-3">Reactants</th>
                <th className="py-1 pr-3">Products</th>
                <th className="py-1">Match</th>
              </tr>
            </thead>
            <tbody>
              {tally.length === 0 ? (
                <tr>
                  <td colSpan={4} className="py-2 font-medium text-slate2">
                    Enter whole numbers from {MIN_COEFFICIENT} to {MAX_COEFFICIENT} to count atoms.
                  </td>
                </tr>
              ) : (
                tally.map((row) => (
                  <tr key={row.element} className="border-t border-hairline font-bold text-ink">
                    <td className="py-1.5 pr-3">{row.name}</td>
                    <td className="py-1.5 pr-3 tabular-nums">{row.left}</td>
                    <td className="py-1.5 pr-3 tabular-nums">{row.right}</td>
                    <td className="py-1.5">{row.balanced ? '✓ equal' : '✗ different'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <button
          type="button"
          onClick={() => setCheck(checkProductionBalance(selected, coefficients))}
          className="btn-pill-primary mt-5"
        >
          Check the balance
        </button>

        {check ? (
          <div className="mt-5 grid gap-4">
            <FeedbackPanel good={check.status === 'balanced'} title={STATUS_TITLES[check.status]} label="Balance feedback">
              <p>{check.feedback}</p>
            </FeedbackPanel>
            {check.status === 'balanced' ? (
              <div className="rounded-2xl border border-hairline bg-surface p-4 text-sm font-medium leading-6 text-slate2">
                <p>
                  <strong className="text-ink">Typical conditions:</strong> {selected.conditions}
                </p>
              </div>
            ) : null}
          </div>
        ) : null}
      </section>
    </div>
  );
}
